import React, { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Cell } from '../../components/InlineCells';
import { ReportNavContext } from '../../components/reportNav';
import { getBsView } from '../../api/financials_bs';
import { useP } from '../../i18n/useP';
import '../../components/knowledge/KnowledgeDrawer.css';
import './HypothesesPanel.css';

/* LE VOLET DES HYPOTHÈSES (décision Olivier, 2026-09-27) : il s'ouvre à droite
   et POUSSE la table, au lieu d'une fenêtre qui la recouvrait ; on change un
   taux ou le point de départ et l'on voit le plan se recalculer à côté.
   Il porte ce qui vaut pour tout le groupe Finance de Ventes & Ops : la
   trésorerie (point de départ, seuil minimal cible), les charges (taux de
   charges sociales, taux d'impôt), le solde ouvert des charges sociales, et la
   TVA en lecture (elle se règle dans les Paramètres du client).
   Les réglages propres à un poste (profils d'encaissement, délais, reprise
   des acomptes) restent dans leur détail, à côté du chiffre qu'ils font bouger.
   Point de départ, seuil et solde ouvert valent pour l'EXERCICE affiché ; les
   deux taux sont portés par leur ligne du compte de résultat, pour toutes les
   années. */

const PERIOD = { monthly: 'mensuel', quarterly: 'trimestriel', semiannual: 'semestriel', annual: 'annuel' };

export default function HypothesesPanel({ activeTenant, year, unit = 'kchf', a, setKeyNow, onChanged,
  rateSocial, rateTax, csReal, reloadKey = 0, onClose }) {
  const p = useP();
  const nav = useContext(ReportNavContext);
  const tid = activeTenant?.id;
  const [bs, setBs] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!tid) return () => {};
    getBsView(tid, year, { flatForecast: true })
      .then((r) => { if (alive) setBs(r && !r.error ? r.data : null); })
      .catch(() => { if (alive) setBs(null); });
    return () => { alive = false; };
  }, [tid, year, reloadKey]);

  const div = unit === 'chf' ? 1 : 1000;
  const fmt = (v) => (v == null ? '–' : Math.round(Number(v) / div).toLocaleString('de-CH'));
  const money = (chf) => ({
    value: chf == null ? null : Math.round(Number(chf) / div), type: 'number',
    format: (x) => (x == null ? '' : Number(x).toLocaleString('de-CH')),
    toDraft: (x) => (x == null ? '' : String(x)),
  });
  const save = (key, value) => Promise.resolve(setKeyNow(key, value)).then(() => onChanged && onChanged());

  // ── Trésorerie ──────────────────────────────────────────────────────────
  const buffer = Number(a?.cash_buffer) || 0;

  // ── Solde ouvert des charges sociales (venu du détail, 2026-09-27) ──────
  const cfg = (a?.social_cash_plan && typeof a.social_cash_plan === 'object') ? a.social_cash_plan : {};
  const lines = Array.isArray(cfg.lines) ? cfg.lines : [];
  const modes = (cfg.modes && typeof cfg.modes === 'object') ? cfg.modes : {};
  const cc = Number(bs?.closed_count) || 0;
  const cands = (bs?.lines || []).filter((l) => ['current_liabilities', 'long_term_liabilities', 'current_assets'].includes(l.section));
  const sign = (l) => (l.section === 'current_assets' ? -1 : 1);
  const lastReal = (id) => {
    const r = (bs?.rows || []).find((x) => x.code === id);
    return Number(cc >= 1 ? r?.months?.[String(cc)] : r?.prev_dec) || 0;
  };
  const modeOf = (id) => (modes[id] === 'manual' ? 'manual' : 'auto');
  const tracked = lines.map((id) => cands.find((l) => l.id === id)).filter(Boolean);
  const setCfg = (patch) => save('social_cash_plan', { lines, modes, ...patch });

  // ── TVA (Paramètres du client) ──────────────────────────────────────────
  const vatRate = Number(activeTenant?.vat_rate);
  const lag = Number(activeTenant?.vat_payment_lag);
  const goVat = () => {
    try { window.localStorage.setItem('fin_settings_tab', JSON.stringify('vat')); } catch (e) { /* quota */ }
    if (nav?.goSettings) nav.goSettings('finance');
  };

  /* UN VRAI VOLET (demande d'Olivier, 2026-09-27) : ancré au bord droit de
     la fenêtre, sur toute la hauteur, au costume du panneau Savoirs (entête,
     croix cadrée, corps qui défile). Il se monte à côté de la zone de travail,
     qu'il pousse, et non dans la page, où il se lisait comme une carte. */
  const panel = (
    <aside className="hyp" aria-label={p('Hypothèses')}>
      <div className="kd-head">
        <span className="kd-title">{p('Hypothèses')}</span>
        <button type="button" className="kd-icon" aria-label={p('Fermer')} onClick={onClose}><X size={15} strokeWidth={1.8} /></button>
      </div>
      <div className="kd-body hyp__body">

      <section className="hyp__g">
        <div className="hyp__gl">{p('Trésorerie')} · {year}</div>
        {/* La trésorerie de départ est partie au Point de départ du plan de
            liquidité (2026-09-28), avec les postes ouverts. */}
        <div className="hyp__it">
          <span>{p('Seuil minimal cible')}</span>
          <span className="hyp__v">
            <Cell {...money(buffer || null)} canWrite placeholder="0"
                  onSave={(v) => save('cash_buffer', v == null ? 0 : v * div)} />
          </span>
        </div>
      </section>

      <section className="hyp__g">
        <div className="hyp__gl">{p('Charges')}</div>
        {/* Avec People, les charges sociales viennent de Salaires prévisionnels
            (2026-10-03) : pas de taux à poser, le champ disparaît. */}
        {rateSocial != null && (
          <div className="hyp__it"><span>{p('Taux de charges sociales')}</span><span className="hyp__v">{rateSocial}</span></div>
        )}
        {rateSocial != null && csReal != null && <div className="hyp__ref">{p('constaté sur le réel')} : {(csReal * 100).toFixed(1)} %</div>}
        <div className="hyp__it"><span>{p("Taux d'impôt")}</span><span className="hyp__v">{rateTax}</span></div>
      </section>

      <section className="hyp__g">
        <div className="hyp__gl">{p('Charges sociales')} · {p('solde ouvert')}</div>
        {tracked.map((l) => (
          <div key={l.id} className="hyp__it">
            <span>{l.label}</span>
            <span className="hyp__v hyp__v--row">
              {fmt(sign(l) * lastReal(l.id))}
              <button type="button" className="hyp__lk"
                      title={p('Basculer entre calculé et tenu à la main')}
                      onClick={() => setCfg({ modes: { ...modes, [l.id]: modeOf(l.id) === 'auto' ? 'manual' : 'auto' } })}>
                {modeOf(l.id) === 'auto' ? p('calculé') : p('manuel')}
              </button>
              <button type="button" className="lk-x" aria-label={p('Retirer')}
                      onClick={() => setCfg({ lines: lines.filter((x) => x !== l.id) })}>✕</button>
            </span>
          </div>
        ))}
        <select className="hyp__add" value=""
                onChange={(e) => e.target.value && setCfg({ lines: [...lines, e.target.value] })}>
          <option value="">{p('＋ ajouter un poste')}</option>
          {cands.filter((l) => !lines.includes(l.id)).map((l) => (
            <option key={l.id} value={l.id}>{`${l.label} · ${fmt(sign(l) * lastReal(l.id))}`}</option>
          ))}
        </select>
      </section>

      <section className="hyp__g">
        <div className="hyp__gl">{p('Réglages du client')}</div>
        <div className="hyp__it">
          <span>{p('TVA')}{Number.isFinite(vatRate) ? ` ${(vatRate * 100).toFixed(1)} %` : ''} · {p(PERIOD[activeTenant?.vat_periodicity] || 'trimestriel')}{Number.isFinite(lag) ? ` · ${lag * 30} ${p('jours')}` : ''}</span>
          <button type="button" className="hyp__lk" onClick={goVat}>{p('Paramètres')} ›</button>
        </div>
      </section>
      </div>
    </aside>
  );
  const host = typeof document !== 'undefined' ? document.querySelector('.app-body') : null;
  return host ? createPortal(panel, host) : panel;
}
